<template>
  <nav
    ref="pagination"
    role="navigation"
    aria-label="pagination"
    class="the-pagination"
  >
    <template v-if="data">
      <template v-if="pages.length">
        <div
          :class="{ 'is-mobile': isMobile }"
          class="the-pagination__total"
        >
          <template v-if="hasLimit">
            <span>
              <span v-if="!isMobile">
                Itens por página
              </span>
              <base-select
                v-model="limit"
                :options="options"
                class="the-pagination__select"
                @input="setLimitToStorage"
              />
            </span>
          </template>
          <template v-else>
            <span>Resultados</span>
          </template>
          <span>
            <span>{{ getInfo('from') }}</span>
            até <span>{{ getInfo('to') }}</span>
            de <span>{{ getInfo('total') }}</span>
          </span>
        </div>
      </template>
      <ul class="the-pagination__list">
        <template v-for="page in pages">
          <template v-if="page">
            <li :key="linkKey(page.label)">
              <button
                class="the-pagination__link"
                :disabled="!page.url"
                :class="{ 'is-active': page.active }"
                @click="setPageToStorage(page.url)"
                v-html="sanitizedContent(page.label)"
              />
            </li>
          </template>
        </template>
      </ul>
    </template>
  </nav>
</template>

<script>
import { mapGetters } from 'vuex';

import sanitizeHtmlMixin from '@/mixins/sanitizeHtml.mixin';

import BaseSelect from '@/components/Base/Select/index.vue';

export default {
  name: 'ThePagination',
  components: {
    BaseSelect,
  },
  mixins: [
    sanitizeHtmlMixin,
  ],
  props: {
    data: {
      type: Object,
      required: true,
    },
    storageKey: {
      type: String,
      default: 'filters',
      required: false,
    },
    hasLimit: {
      type: Boolean,
      default: false,
      required: false,
    },
    persistState: {
      type: Boolean,
      default: true,
      required: false,
    },
  },
  data: () => ({
    limit: 50,
    options: [
      { value: 25, name: '25' },
      { value: 50, name: '50' },
      { value: 75, name: '75' },
      { value: 100, name: '100' },
    ],
  }),
  computed: {
    ...mapGetters({
      isMobile: 'app/isMobile',
    }),
    pages() {
      let pages = [...(this?.data?.links ?? [])];
      let prev;
      let next;
      let first = [];
      let last = [];

      if (pages.length) {
        prev = pages.shift();
        next = pages.pop();
        prev.label = '&lt;';
        next.label = '&gt;';

        first = this.isMobile
          ? [{
            active: false,
            label: '&laquo;',
            url: this.data.prev_page_url
              ? this.data.first_page_url
              : null,
          }]
          : [];
        last = this.isMobile
          ? [{
            active: false,
            label: '&raquo;',
            url: this.data.next_page_url
              ? this.data.last_page_url
              : null,
          }]
          : [];

        if (this.isMobile && pages.length > 4) {
          pages = [
            pages[0],
            pages[1],
            {
              active: false,
              label: '...',
              url: null,
            },
            pages[pages.length - 1],
          ];
        }
      }

      return [
        ...(first),
        ...(prev ? [prev] : []),
        ...pages,
        ...(next ? [next] : []),
        ...(last),
      ];
    },
  },
  mounted() {
    this.setLimit();
  },
  methods: {
    // storage
    setPageToStorage(url) {
      let page;

      decodeURIComponent(url)
        .split('?')
        .pop()
        .split('&')
        .map((item) => item.split('='))
        .forEach(([key, value]) => {
          if (key === 'page') {
            page = Number(value);
          }
        });

      if (this.persistState) {
        let dataStorage = sessionStorage.getItem(this.storageKey);

        dataStorage = dataStorage
          ? JSON.parse(dataStorage)
          : {};

        dataStorage.page = Number.isFinite(page) ? page : undefined;
        sessionStorage.setItem(this.storageKey, JSON.stringify(dataStorage));
      }

      this.$emit('paginate', {
        page: Number.isFinite(page) ? page : undefined,
        per_page: this.limit,
      });
    },
    setLimitToStorage() {
      if (this.persistState) {
        let dataStorage = sessionStorage.getItem(this.storageKey);

        dataStorage = dataStorage
          ? JSON.parse(dataStorage)
          : {};

        dataStorage.limit = this.limit;
        sessionStorage.setItem(this.storageKey, JSON.stringify(dataStorage));
      }

      this.$emit('paginate', {
        page: 1,
        per_page: this.limit,
      });
    },

    // handlers
    linkKey(text) {
      return `${text}-${Math.random(1000)}`;
    },
    getInfo(target) {
      return this.data[target] || '--';
    },
    setLimit() {
      if (this.data && this.data?.per_page) {
        this.limit = this.data.per_page;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
%link {
  font: {
    family: Inter;
    size: 1rem;
    weight: 500;
  }
  color: $gray-500;
  text-decoration: none;
}

%flexbox {
  display: flex;
  align-items: center;
  justify-content: center;
}

%block {
  min-width: 2rem;
  height: 2rem;
  padding: .5rem;
  border-radius: 6px;
}

.the-pagination {
  margin-top: 1.5rem;

  display: flex;
  width: 100%;
  height: fit-content;

  flex-direction: column-reverse;
  gap: 1rem;

  li {
    @extend %flexbox;

    list-style: none;
  }

  button {
    border: none;
  }

  @media (min-width: $breakpoint-desktop) {
    flex-direction: row;

    > *:last-child {
      margin-left: auto;
    }
  }

  &__list,
  &__link,
  &__total,
  &__total span {
    @extend %flexbox;
  }

  &__link,
  &__total,
  &__total span > span {
    @extend %link;
  }

  &__link,
  &__total span > span {
    @extend %block;
  }

  &__list {
    flex-wrap: wrap;
    gap: .5rem;
  }

  &__select {
    width: 85px;
    min-height: fit-content !important;
  }

  &__link {
    background-color: #fff;

    white-space: nowrap;
    cursor: pointer;

    &.is-active {
      background-color: $prata-green-100;

      font-weight: 700;
      color: $prata-green-600;
    }

    &:disabled {
      background-color: $gray-100;

      color: $gray-400;
      pointer-events: none;
    }

    &:hover,
    &:active {
      background-color: $prata-green-50;
      color: $prata-green-600;
    }
  }

  &__total {
    > span {
      > span {
        margin: 0 .5rem;
        background-color: $gray-100;
      }
    }

    &.is-mobile {
      gap: 1rem;
    }
  }
}
</style>
